import { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAdminAccess } from '@/features/auth';
import { cn } from '@/shared/lib/cn';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { Scroller } from '@/shared/ui';
import styles from './AdminLayout.module.css';

export const AdminLayoutPage: React.FC = () => {
  const { groups: visibleGroups, canAccess } = useAdminAccess();
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const narrow = useMediaQuery('(max-width: 860px)');

  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  const groupCount = visibleGroups.length;
  useEffect(() => {
    let frame = 0;
    let attempts = 60;
    const reveal = () => {
      const viewport = navRef.current;
      const link = viewport?.querySelector<HTMLElement>('[aria-current="page"]');
      if (!viewport || !link) return;
      const box = viewport.getBoundingClientRect();
      const item = link.getBoundingClientRect();
      const hidden =
        item.left < box.left ||
        item.right > box.right ||
        item.top < box.top ||
        item.bottom > box.bottom;
      if (!hidden) return;
      viewport.scrollTo({
        left: viewport.scrollLeft + item.left - box.left - (box.width - item.width) / 2,
        top: viewport.scrollTop + item.top - box.top - (box.height - item.height) / 2,
      });
      if (--attempts > 0) frame = requestAnimationFrame(reveal);
    };
    reveal();
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, narrow, groupCount]);

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.layout}>
      <Scroller
        element="aside"
        axis="both"
        className={styles.sidebar}
        data-tour="admin-nav"
        viewportClassName={styles.sidebarViewport}
        viewportRef={navRef}
      >
        {visibleGroups.map((group) => (
          <nav className={styles.navGroup} key={group.label} aria-label={group.label}>
            <div className={styles.navGroupLabel}>{group.label}</div>
            <div className={styles.nav}>
              {group.items.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={cn(styles.navLink, active && styles.navLinkActive)}
                    aria-current={active ? 'page' : undefined}
                  >
                    <item.Icon className={styles.navIcon} size={16} aria-hidden />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>
        ))}
      </Scroller>
      { }
      {narrow ? (
        <main className={styles.main}>
          <Outlet />
        </main>
      ) : (
        <Scroller
          element="main"
          className={styles.main}
          viewportRef={mainRef}
        >
          <Outlet />
        </Scroller>
      )}
    </div>
  );
};
